import { shouldHydrateCachedValue } from '@/utils/waterYears'

export const OFFLINE_FIELD_STORES = [
  'sectionSchemas',
  'users',
  'syncHistory',
  'omsettings',
  'flags',
  'notes',
  'contacts',
  'divisions',
  'ditches',
  'diversionPoints',
  'states',
  'farmunits',
  'orders',
  'usages',
  'announcements',
  'offlineRequests'
]

export const OFFLINE_CACHE_REQUIRED_STORES = [
  'sectionSchemas',
  'omsettings',
  'contacts',
  'divisions',
  'ditches',
  'diversionPoints',
  'farmunits'
]

export const OFFLINE_CACHE_OVERRIDES = {
  system: ['waterYear', 'availableWaterYears', 'offlinePushRequests'],
  omsettings: ['omOfYear']
}

export const OFFLINE_CACHE_MANIFEST_KEY = 'offline-cache-manifest'

export const OFFLINE_PUSH_REQUESTS_KEY = 'system-offlinePushRequests'

export const countCacheItems = (value) => {
  if (Array.isArray(value)) {
    return value.length
  }
  if (value && typeof value === 'object') {
    return Object.keys(value).length
  }
  return 0
}

const relationId = (value) => {
  if (!value) {
    return value
  }
  if (typeof value === 'object') {
    return value.id
  }
  return value
}

export const buildOfflineCacheManifest = (rootState, user = {}) => {
  const storeCounts = {}
  OFFLINE_FIELD_STORES.forEach((store) => {
    storeCounts[store] = countCacheItems(rootState[store]?.all)
  })

  return {
    version: process.env.npm_package_version || 'unknown',
    savedAt: new Date().toISOString(),
    waterYear: rootState.system?.waterYear || null,
    userId: user?.id || null,
    storeCounts,
    profile: 'ditchrider-field-data'
  }
}

export const validateOfflineCache = (rootState, manifest = null) => {
  const missingStores = []
  const relationIssues = []

  OFFLINE_CACHE_REQUIRED_STORES.forEach((store) => {
    if (countCacheItems(rootState[store]?.all) === 0) {
      missingStores.push(store)
    }
  })

  const farmunits = rootState.farmunits?.all || {}
  const ditches = rootState.ditches?.all || {}
  const divisions = rootState.divisions?.all || {}
  const diversionPoints = Object.values(rootState.diversionPoints?.all || {})

  diversionPoints.forEach((dp) => {
    const farmunitId = relationId(dp.farmunit)
    const ditchId = relationId(dp.ditch)
    const divisionId = relationId(dp.division)

    if (farmunitId && !farmunits[farmunitId]) {
      relationIssues.push(`diversionPoint:${dp.id}:farmunit`)
    }
    if (ditchId && !ditches[ditchId]) {
      relationIssues.push(`diversionPoint:${dp.id}:ditch`)
    }
    if (divisionId && !divisions[divisionId]) {
      relationIssues.push(`diversionPoint:${dp.id}:division`)
    }
  })

  return {
    ready: missingStores.length === 0 && relationIssues.length === 0,
    checkedAt: new Date().toISOString(),
    savedAt: manifest?.savedAt || null,
    waterYear: manifest?.waterYear || rootState.system?.waterYear || null,
    userId: manifest?.userId || null,
    storeCounts: manifest?.storeCounts || {},
    missingStores,
    relationIssues
  }
}

export const persistOfflineCache = async (localForage, rootState, user = {}) => {
  for (const store of OFFLINE_FIELD_STORES) {
    if (rootState[store] && Object.prototype.hasOwnProperty.call(rootState[store], 'all')) {
      await localForage.setItem(store, rootState[store].all)
    }
  }

  for (const [store, keys] of Object.entries(OFFLINE_CACHE_OVERRIDES)) {
    for (const key of keys) {
      await localForage.setItem(`${store}-${key}`, rootState[store]?.[key])
    }
  }

  const manifest = buildOfflineCacheManifest(rootState, user)
  await localForage.setItem(OFFLINE_CACHE_MANIFEST_KEY, manifest)
  return manifest
}

export const persistOfflinePushRequests = async (localForage, offlinePushRequests = []) => {
  if (!localForage || typeof localForage.setItem !== 'function') {
    return null
  }

  return await localForage.setItem(OFFLINE_PUSH_REQUESTS_KEY, offlinePushRequests)
}

export const hydrateOfflineCache = async (localForage, store, { online = false } = {}) => {
  const hydratedStores = []

  for (const [storeKey, keys] of Object.entries(OFFLINE_CACHE_OVERRIDES)) {
    for (const key of keys) {
      const value = await localForage.getItem(`${storeKey}-${key}`)
      const isWaterYearState = storeKey === 'system' && ['waterYear', 'availableWaterYears'].includes(key)
      if (value !== null && value !== undefined && (
        !isWaterYearState || shouldHydrateCachedValue({ online, currentValue: store.state[storeKey]?.[key] })
      )) {
        store.commit(`${storeKey}/SET_DYNAMIC`, { key, value })
      }
    }
  }

  for (const storeKey of OFFLINE_FIELD_STORES) {
    if (!store.state[storeKey] || !Object.prototype.hasOwnProperty.call(store.state[storeKey], 'all')) {
      continue
    }

    const all = await localForage.getItem(storeKey)
    if (all !== null && all !== undefined && (
      storeKey !== 'omsettings' || shouldHydrateCachedValue({ online, currentValue: store.state[storeKey].all })
    )) {
      store.commit(`${storeKey}/REPLACE_ALL`, all)
      hydratedStores.push(storeKey)
    }
  }

  const manifest = await localForage.getItem(OFFLINE_CACHE_MANIFEST_KEY)
  return {
    hydratedStores,
    manifest
  }
}
